import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';

const Contact: React.FC = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formState),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }
      
      if (data.success) {
        setIsSubmitted(true);
        setFormState({
          name: '',
          email: '',
          company: '',
          message: '',
        });
      } else {
        throw new Error(data.error || 'Failed to send message');
      }
    } catch (err) {
      setError('An error occurred while sending your message. Please try again later.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="contact" className="mt-8 sm:mt-10">
      <div className="max-w-container mx-auto">
        <div className="mt-6 sm:mt-10">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="lg:grid lg:grid-cols-2">
              <div className="py-8 px-4 sm:px-6 lg:px-8 xl:p-10 bg-primary-700 order-2 lg:order-1">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="text-primary"
                >
                  <h3 className="text-xl sm:text-2xl font-extrabold text-primary">Contact Information</h3>
                  <p className="mt-3 sm:mt-4 text-base sm:text-lg">
                    Have questions about our lab results processing platform? Our clinical microbiology experts are here to help you implement our solution in your laboratory workflow.
                  </p>
                  <dl className="mt-6 sm:mt-8 space-y-4 sm:space-y-6">
                    <dt><span className="sr-only">Email</span></dt>
                    <dd className="flex text-sm sm:text-base">
                      <svg className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 text-primary-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <a href="mailto:support@reasonshield.com" className="ml-3 hover:text-primary-400 transition-colors">support@reasonshield.com</a>
                    </dd>
                    <dt><span className="sr-only">Phone number</span></dt>
                    <dd className="flex text-sm sm:text-base">
                      <svg className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 text-primary-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <a href="tel:+17085403586" className="ml-3 hover:text-primary-400 transition-colors">+1 (708) 540-3586</a>
                    </dd>
                    <dt><span className="sr-only">Address</span></dt>
                    <dd className="flex text-sm sm:text-base">
                      <svg className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 text-primary-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="ml-3">
                        1449 S Michigan Ave STE 13207<br />
                        Chicago, IL 60605
                      </span>
                    </dd>
                  </dl>
                </motion.div>
              </div>

              <div className="py-8 px-4 sm:px-6 lg:px-8 xl:p-10 order-1 lg:order-2">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900">Send us a message</h3>
                  
                  {isSubmitted ? (
                    <div className="mt-6 bg-green-50 border border-green-200 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-green-800">Message sent successfully</h3>
                          <div className="mt-2 text-sm text-green-700">
                            <p>Thank you for contacting us! We'll get back to you as soon as possible.</p>
                          </div>
                          <div className="mt-4">
                            <button
                              type="button"
                              onClick={() => setIsSubmitted(false)}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                              Send another message
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="mt-5 sm:mt-6 grid grid-cols-1 gap-y-4 sm:gap-y-6 sm:grid-cols-2 sm:gap-x-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                          Name
                        </label>
                        <div className="mt-1 border-2 border-primary-500 rounded-md" >
                          <input
                            type="text"
                            name="name"
                            id="name"
                            autoComplete="name"
                            required
                            value={formState.name}
                            onChange={handleChange}
                            className="py-2 sm:py-3 px-3 sm:px-4 block w-full text-sm sm:text-base shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                      <div >
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                          Email
                        </label>
                        <div className="mt-1 border-2 border-primary-500 rounded-md">
                          <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={formState.email}
                            onChange={handleChange}
                            className="py-2 sm:py-3 px-3 sm:px-4 block w-full text-sm sm:text-base shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="company" className="block text-sm font-medium text-text">
                          Organization / Laboratory
                        </label>
                        <div className="mt-1 border-2 border-primary-500 rounded-md">
                          <input
                            type="text"
                            name="company"
                            id="company"
                            autoComplete="organization"
                            value={formState.company}
                            onChange={handleChange}
                            className="py-2 sm:py-3 px-3 sm:px-4 block w-full text-sm sm:text-base shadow-sm focus:ring-primary focus:border-primary border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                          Message
                        </label>
                        <div className="mt-1 border-2 border-primary-500 rounded-md">
                          <textarea
                            id="message"
                            name="message"
                            rows={3}
                            required
                            value={formState.message}
                            onChange={handleChange}
                            className="py-2 sm:py-3 px-3 sm:px-4 block w-full text-sm sm:text-base shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md"
                          ></textarea>
                        </div>
                      </div>
                      
                      {error && (
                        <div className="sm:col-span-2">
                          <div className="bg-red-50 border border-red-200 rounded-md p-4">
                            <div className="flex">
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">Error</h3>
                                <div className="mt-2 text-sm text-red-700">
                                  <p>{error}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="sm:col-span-2">
                        <div className="border-2 border-primary rounded-md p-1">
                          <Button
                            variant="primary"
                            size="md"
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full"
                          >
                            {isSubmitting ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-text-light" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Sending...
                              </>
                            ) : 'Submit'}
                          </Button>
                        </div>
                      </div>
                    </form>
                  )}
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact; 